import lodashGet from "lodash/get";
import lodashHas from "lodash/has";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashFind from "lodash/find";

import {
  getNotifications,
  addNotification,
  updateNotification,
  deleteNotification,
} from "@/services/notifications/notifications.service";
import { NOTIFICATION_TYPE_SAVED_SEARCH } from "@/common/constants/notifications";
import { isFeatureUnlocked } from "@/common/featureLock.js";
import { httpPost } from "@/services/api.service";
import { getUserProfile } from "@/common/storage";
import {
  getSavedSearches,
  addSavedSearch,
  updateSavedSearch,
  deleteSavedSearch,
  shareSavedSearch,
} from "@/common/constants/graphql/savedSearch";
import { ENDPOINT_SAVED_SEARCH } from "@/common/constants/api";
import { getFormatted } from "@/common/date.utilities";

export { _saveSearchQuery as saveSearchQuery };
export { _getSavedSearchList as getSavedSearchList };
export { _deleteSearchQuery as deleteSearchQuery };
export { _updateSearchQuery as updateSearchQuery };
export { _shareSearchQuery as shareSearchQuery };
export { _getKeywordsStringFromBuilder as getKeywordsStringFromBuilder };
export { _formatSavedSearch as formatSavedSearch };

const _saveSearchQuery = async (paramsIn) => {
  try {
    let params = lodashCloneDeep(paramsIn);
    params.formatted = params.formatted || {};
    params.formatted.docType = NOTIFICATION_TYPE_SAVED_SEARCH;
    params.formatted.enabled = false;
    params.formatted.name = params.name;

    // using the places property for both assets and places
    if (params?.params?.assets) {
      params.params.places = lodashCloneDeep(params.params.assets);
      params.params.boundsType = "assets";
      delete params.params.assets;
    }

    if (isFeatureUnlocked("saved-search-new-api")) {
      const currentUser = getUserProfile();
      const result = await httpPost({
        url: ENDPOINT_SAVED_SEARCH,
        data: {
          query: addSavedSearch,
          variables: {
            addsavedsearchinput: {
              params: params.params ? JSON.stringify(params.params) : "{}",
              name: params.formatted.name ? params.formatted.name : "",
              description: "",
              ownerId: currentUser?.UserID ? currentUser.UserID : "",
            },
          },
        },
      });
      const { data } = JSON.parse(result);
      let savedSearch;
      if (data?.data?.addSearch?.id) {
        const searches = await _getSavedSearchList();
        savedSearch = lodashFind(searches, ["id", data.data.addSearch.id]);
      }
      return savedSearch;
    }

    return await addNotification(params);
  } catch (error) {
    console.error("Unable to create search query", error);
    return false;
  }
};

const _getSavedSearchList = async () => {
  try {
    if (isFeatureUnlocked("saved-search-new-api")) {
      const response = await httpPost({
        url: ENDPOINT_SAVED_SEARCH,
        data: {
          query: getSavedSearches,
        },
      });
      const { data } = JSON.parse(response);

      return data?.data?.searches?.mySearches?.length
        ? _formatSavedSearches(data?.data?.searches?.mySearches)
        : [];
    }

    return await getNotifications({
      type: [{ value: NOTIFICATION_TYPE_SAVED_SEARCH }],
    });
  } catch (error) {
    console.error("Unable to get users saved search", error);
    return false;
  }
};

const _formatSavedSearches = (searches) => {
  return searches.map((s) => _formatSavedSearch(s));
};

const _deleteSearchQuery = async (params) => {
  try {
    if (!params || !params.id) {
      console.error("Unable to delete search query, id not set.");
      return false;
    }

    params.formatted = params.formatted || {};
    params.formatted.id = params.id;

    if (isFeatureUnlocked("saved-search-new-api")) {
      return await httpPost({
        url: ENDPOINT_SAVED_SEARCH,
        data: {
          query: deleteSavedSearch,
          variables: {
            deletesavedsearchid: params.id,
          },
        },
      });
    }

    return await deleteNotification(params);
  } catch (error) {
    console.error("Unable to delete search query", error);
    return false;
  }
};

const _updateSearchQuery = async (paramsIn) => {
  try {
    let params = lodashCloneDeep(paramsIn);
    if (!params || !params.id) {
      console.error("Unable to update search query, id not set.");
      return false;
    }

    params.formatted = params.formatted || {};
    params.formatted.docType = NOTIFICATION_TYPE_SAVED_SEARCH;
    params.formatted.id = params.id;
    if (params.data) {
      params.formatted.enabled = lodashHas(params, "data.enabled")
        ? params.data.enabled
        : false;
      params.formatted.name = lodashHas(params, "data.name")
        ? params.data.name
        : null;
      params.formatted.description = lodashHas(params, "data.description")
        ? params.data.description
        : null;
      if (lodashHas(params, "data.sharedList")) {
        params.formatted.sharedList = lodashCloneDeep(params.data.sharedList);
      }
      if (lodashHas(params, "data.frequency")) {
        params.formatted.frequency = params.data.frequency;
      }
      if (lodashHas(params, "data.schedule")) {
        params.formatted.schedule = params.data.schedule;
      }
      if (lodashHas(params, "data.timeOfDay")) {
        params.formatted.timeOfDay = params.data.timeOfDay;
      }
      if (lodashHas(params, "data.dayOfWeek")) {
        params.formatted.dayOfWeek = params.data.dayOfWeek;
      }
      if (lodashHas(params, "data.expiration")) {
        params.formatted.expiration = params.data.expiration;
      }
      if (lodashHas(params, "data.expirationDate")) {
        params.formatted.expirationDate = params.data.expirationDate;
      }
      params.formatted.delivery = [];
      if (lodashHas(params, "data.useEmail")) {
        params.formatted.delivery.push("email");
      }
      if (lodashHas(params, "data.usePush")) {
        params.formatted.delivery.push("push");
      }
      if (lodashHas(params, "data.useIntegrations")) {
        params.formatted.delivery.push("target");
      }
      if (lodashHas(params, "data.integrationTargets")) {
        params.formatted.targets = params.data.integrationTargets;
      }
      if (lodashHas(params, "data.params")) {
        params.params = params.data.params;

        // using the places property for both assets and places
        if (params?.params?.assets) {
          params.params.places = lodashCloneDeep(params.params.assets);
          params.params.boundsType = "assets";
          delete params.params.assets;
        }
      }
      delete params.data;
    }

    if (isFeatureUnlocked("saved-search-new-api")) {
      const input = {
        id: params.formatted.id,
        params: params.params ? JSON.stringify(params.params) : "{}",
        name: params.formatted.name ? params.formatted.name : "",
        description: params.formatted.description
          ? params.formatted.description
          : "",
      };
      const response = await httpPost({
        url: ENDPOINT_SAVED_SEARCH,
        data: {
          query: updateSavedSearch,
          variables: {
            updatesavedsearchinput: input,
          },
        },
      });
      const { data } = JSON.parse(response);
      let savedSearch;
      if (data?.data?.updateSearch?.id) {
        const searches = await _getSavedSearchList();
        savedSearch = lodashFind(searches, ["id", data.data.updateSearch.id]);
      }
      return savedSearch;
    }

    const notification = await updateNotification(params);
    return notification;
  } catch (error) {
    console.error("Unable to update search query", error);
    return false;
  }
};

const _shareSearchQuery = async (params) => {
  try {
    if (!params || !params.id) {
      console.error("Unable to share search query, id not set.");
      return false;
    }

    const response = await httpPost({
      url: ENDPOINT_SAVED_SEARCH,
      data: {
        query: shareSavedSearch,
        variables: {
          sharesavedsearchid: params.id,
          sharesavedsearchsharedwith: params.sharedWith
            ? params.sharedWith
            : [],
        },
      },
    });
    return response;
  } catch (error) {
    console.error("Unable to share search query", error);
    return false;
  }
};

const _getKeywordsStringFromBuilder = (keywordsBuilder) => {
  let keywordsStr = "";
  if (keywordsBuilder) {
    let keywordsParts = [];
    if (lodashGet(keywordsBuilder, "includes")) {
      const includeGroups = [];
      keywordsBuilder.includes.forEach((group) => {
        if (lodashGet(group, "keywords") && group.keywords.length) {
          const includeWords = [];
          group.keywords.forEach((keyword) => {
            if (lodashGet(keyword, "text")) {
              includeWords.push(`"${keyword.text}"`);
            }
          });
          includeGroups.push(`+(${includeWords.join(" | ")})`);
        }
      });
      if (includeGroups.length) {
        keywordsParts.push(includeGroups.join(" "));
      }
    }
    if (lodashGet(keywordsBuilder, "excludes")) {
      const excludeWords = [];
      keywordsBuilder.excludes.forEach((keyword) => {
        if (lodashGet(keyword, "text")) {
          excludeWords.push(`-"${keyword.text}"`);
        }
      });
      keywordsParts.push(excludeWords.join(" "));
    }
    keywordsStr = keywordsParts.join(" ");
  }
  return keywordsStr;
};

/**
 * Formats a saved search object into a notification object
 * @param savedSearch
 * @returns {{docType: string, formatted: (*&{name, id, lastUpdateTime?: string}), searchNotificationId, params: {[p: number]: any, [p: symbol]: any}}}
 */
const _formatSavedSearch = (savedSearch) => {
  const { places, ...params } = JSON.parse(savedSearch.params);

  return {
    ...savedSearch,
    docType: NOTIFICATION_TYPE_SAVED_SEARCH,
    searchNotificationId: savedSearch.id,
    params: {
      ...params,
      ...(places && params.boundsType === "assets" && { assets: places }),
    },
    formatted: {
      ...(savedSearch.formatted ?? {}),
      id: savedSearch.id,
      name: savedSearch.name,
      ...(savedSearch.createdAt && {
        creationTime: getFormatted(savedSearch.createdAt),
      }),
      ...(savedSearch.updatedAt
        ? { lastUpdateTime: getFormatted(savedSearch.updatedAt) }
        : savedSearch.createdAt
        ? { lastUpdateTime: getFormatted(savedSearch.createdAt) }
        : {}),
    },
  };
};
